<template>
  <div class="user-create-container">
    <div class="control-panel">
      <div class="control-container">
        <div class="header-info">
          <i class="el-icon-plus"></i>
          <span>{{ $t('userManagement.createUser') }}</span>
        </div>
        <div class="action-wrapper">
          <el-button @click="handleBack" size="small">
            <i class="el-icon-back"></i> {{ $t('common.back') }}
          </el-button>
        </div>
      </div>
    </div>

    <el-card class="user-create-card" shadow="hover">
      <el-form
        ref="userForm"
        :model="userForm"
        :rules="userFormRules"
        label-position="top"
        size="large"
      >
        <div class="form-module">
          <div class="module-title">{{ $t('userManagement.basicInfo') }}</div>
          <el-row :gutter="20">
            <el-col :span="24">
              <el-form-item :label="$t('userManagement.avatar')" prop="avatar">
                <avatar-upload v-model="userForm.avatar"></avatar-upload>
              </el-form-item>
            </el-col>
            <el-col :xs="24" :sm="12" :md="8">
              <el-form-item :label="$t('userManagement.username')" prop="username">
                <el-input v-model="userForm.username" :placeholder="$t('userManagement.enterUsername')" clearable />
              </el-form-item>
            </el-col>
            <el-col :xs="24" :sm="12" :md="8">
              <el-form-item :label="$t('userManagement.name')" prop="name">
                <el-input v-model="userForm.name" :placeholder="$t('userManagement.inputName')" clearable />
              </el-form-item>
            </el-col>
            <el-col :xs="24" :sm="12" :md="8">
              <el-form-item :label="$t('userManagement.gender')" prop="gender">
                <el-radio-group v-model="userForm.gender">
                  <el-radio :label="1">{{ $t('userManagement.male') }}</el-radio>
                  <el-radio :label="2">{{ $t('userManagement.female') }}</el-radio>
                  <el-radio :label="0">{{ $t('userManagement.unknown') }}</el-radio>
                </el-radio-group>
              </el-form-item>
            </el-col>
            <el-col :xs="24" :sm="12" :md="8">
              <el-form-item :label="$t('userManagement.email')" prop="email">
                <el-input v-model="userForm.email" :placeholder="$t('userManagement.inputEmail')" clearable />
              </el-form-item>
            </el-col>
            <el-col :xs="24" :sm="12" :md="8">
              <el-form-item :label="$t('userManagement.phone')" prop="mobile">
                <el-input v-model="userForm.mobile" :placeholder="$t('userManagement.enterPhone')" clearable />
              </el-form-item>
            </el-col>
          </el-row>
        </div>

        <div class="form-module">
          <div class="module-title">{{ $t('userManagement.passwordSettings') }}</div>
          <el-row :gutter="20">
            <el-col :xs="24" :sm="12" :md="8">
              <el-form-item :label="$t('userManagement.password')" prop="password">
                <el-input v-model="userForm.password" type="password" :placeholder="$t('userManagement.inputPassword')" show-password />
              </el-form-item>
            </el-col>
            <el-col :xs="24" :sm="12" :md="8">
              <el-form-item :label="$t('userManagement.confirmPassword')" prop="confirmPassword">
                <el-input v-model="userForm.confirmPassword" type="password" :placeholder="$t('userManagement.confirmPassword')" show-password />
              </el-form-item>
            </el-col>
          </el-row>
        </div>

        <div class="form-module">
          <div class="module-title">{{ $t('userManagement.userLevel') }} & {{ $t('userManagement.accountBalance') }}</div>
          <el-row :gutter="20">
            <el-col :xs="24" :sm="12" :md="8">
              <el-form-item :label="$t('userManagement.userLevel')" prop="userLevel">
                <el-select v-model="userForm.userLevel" :placeholder="$t('userManagement.selectUserLevel')" style="width: 100%">
                  <el-option v-for="item in levelOptions" :key="item.levelCode" :label="item.levelCode + ' ' + item.levelName" :value="item.levelCode"></el-option>
                </el-select>
              </el-form-item>
            </el-col>
            <el-col :xs="24" :sm="12" :md="8">
              <el-form-item :label="$t('userManagement.accountBalance')" prop="accountBalance">
                <el-input-number v-model="userForm.accountBalance" :precision="2" :step="0.1" :min="0" style="width: 100%"></el-input-number>
              </el-form-item>
            </el-col>
            <el-col :xs="24" :sm="12" :md="8">
              <el-form-item :label="$t('userManagement.points')" prop="points">
                <el-input-number v-model="userForm.points" :step="10" :min="0" :precision="0" style="width: 100%"></el-input-number>
              </el-form-item>
            </el-col>
          </el-row>
        </div>

        <div class="form-module">
          <div class="module-title">{{ $t('roleManagement.permissionConfiguration') }}</div>
          <el-row :gutter="20">
            <el-col :xs="24" :sm="12" :md="8">
              <el-form-item :label="$t('userManagement.role')" prop="roleId">
                <el-select v-model="userForm.roleId" :placeholder="$t('userManagement.selectRole')" style="width: 100%" filterable>
                  <el-option
                    v-for="role in availableRoles"
                    :key="role.id"
                    :label="role.roleName"
                    :value="role.id"
                  ></el-option>
                </el-select>
              </el-form-item>
            </el-col>
            <el-col :xs="24" :sm="12" :md="8">
              <el-form-item :label="$t('userManagement.userGroups')" prop="groupId">
                <el-select v-model="userForm.groupId" :placeholder="$t('userManagement.selectGroup')" style="width: 100%" filterable clearable>
                  <el-option
                    v-for="group in groups"
                    :key="group.id"
                    :label="group.name"
                    :value="group.id"
                  ></el-option>
                </el-select>
              </el-form-item>
            </el-col>
            <el-col :xs="24" :sm="12" :md="8">
              <el-form-item :label="$t('common.status')" prop="status">
                <el-tag type="success" size="medium" effect="dark">
                  {{ $t('common.inUse') }}
                </el-tag>
              </el-form-item>
            </el-col>
          </el-row>
        </div>

        <div class="form-actions">
          <el-button @click="handleBack">{{ $t('common.cancel') }}</el-button>
          <el-button type="primary" @click="handleSubmit" v-if="hasPermission('sys:user:save')">{{ $t('common.submit') }}</el-button>
        </div>
      </el-form>
    </el-card>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import { hasPermission } from '@/utils/permission'
import { getLevelOptions } from '@/apis/system/level'
import AvatarUpload from '@/components/AvatarUpload.vue'

export default {
  name: 'UserCreate',
  components: {
    AvatarUpload
  },
  data() {
    return {
      groups: [],
      levelOptions: [],
      userForm: {
        username: '',
        name: '',
        email: '',
        mobile: '',
        avatar: 'default:1',
        password: '',
        confirmPassword: '',
        roleId: null,
        groupId: null,
        status: 1,
        gender: 0,
        userLevel: 'V0',
        accountBalance: 0,
        points: 0
      },
      userFormRules: {
        username: [
          { required: true, message: this.$t('userManagement.usernameRequired'), trigger: 'blur' },
          { min: 3, max: 64, message: this.$t('userManagement.usernameLength'), trigger: 'blur' },
          { pattern: /^[a-zA-Z0-9_-]+$/, message: this.$t('userManagement.usernamePattern'), trigger: 'blur' }
        ],
        name: [
          { required: true, message: this.$t('userManagement.nameRequired'), trigger: 'blur' },
          { min: 2, max: 50, message: this.$t('userManagement.nameLength'), trigger: 'blur' }
        ],
        email: [
          { required: false, message: this.$t('userManagement.emailRequired'), trigger: 'blur' },
          { type: 'email', message: this.$t('userManagement.emailPattern'), trigger: 'blur' }
        ],
        mobile: [
          { required: false, message: this.$t('userManagement.phoneRequired'), trigger: 'blur' },
          { pattern: /^1[3-9]\d{9}$/, message: this.$t('userManagement.phoneInvalid'), trigger: 'blur' }
        ],
        password: [
          { required: true, message: this.$t('userManagement.passwordRequired'), trigger: 'blur' },
          { min: 6, max: 20, message: this.$t('userManagement.passwordLength'), trigger: 'blur' }
        ],
        confirmPassword: [
          { required: true, message: this.$t('userManagement.confirmPasswordRequired'), trigger: 'blur' },
          { validator: this.validateConfirmPassword, trigger: 'blur' }
        ],
        roleId: [
          { required: true, message: this.$t('userManagement.roleRequired'), trigger: 'change' }
        ],
        groupId: [
          { required: false, message: this.$t('userManagement.groupRequired'), trigger: 'change' }
        ],
        userLevel: [
          { required: true, message: this.$t('userManagement.selectUserLevel'), trigger: 'change' }
        ],
        accountBalance: [
          { required: true, message: this.$t('userManagement.accountBalanceRequired'), trigger: 'blur' }
        ]
      }
    }
  },
  computed: {
    ...mapGetters(['allRoles']),
    availableRoles() {
      return this.allRoles.filter(role => role.roleCode !== 'superAdmin')
    }
  },
  created() {
    this.fetchGroups()
    this.fetchRoles()
    this.fetchLevelOptions()
  },
  methods: {
    hasPermission,
    fetchLevelOptions() {
      getLevelOptions().then(res => {
        this.levelOptions = res || []
      }).catch(() => {})
    },
    // 获取角色列表
    async fetchRoles() {
      if (this.allRoles.length === 0) {
        try {
          await this.$store.dispatch('fetchRoles')
        } catch (error) {
          this.$message.error(this.$t('userManagement.fetchRoleError'))
        }
      }
    },
    // 获取分组列表
    async fetchGroups() {
      try {
        const res = await this.$store.dispatch('fetchUserGroups')
        this.groups = res
      } catch (error) {
        this.$message.error(this.$t('userManagement.fetchGroupError'))
      }
    },
    // 验证确认密码
    validateConfirmPassword(rule, value, callback) {
      if (value !== this.userForm.password) {
        callback(new Error(this.$t('userManagement.passwordNotMatch')))
      } else {
        callback()
      }
    },
    
    // 提交表单
    handleSubmit() {
      this.$refs.userForm.validate((valid) => {
        if (valid) {
          // 移除确认密码字段
          const userData = { ...this.userForm }
          delete userData.confirmPassword
          
          // 将单选的 groupId 转换为后端需要的 groupIdList 数组
          if (userData.groupId) {
            userData.groupIdList = [userData.groupId]
            delete userData.groupId
          } else {
            userData.groupIdList = []
          }
          
          // 提交用户数据
          this.$store.dispatch('addUser', userData).then(() => {
            this.$message.success(this.$t('userManagement.createSuccess'))
            this.handleBack()
          }).catch(err => {
            this.$message.error(this.$t('userManagement.createError'))
          })
        } else {
          return false
        }
      })
    },
    
    // 重置表单
    handleReset() {
      this.$refs.userForm.resetFields()
      this.$nextTick(() => {
        this.$refs.userForm.clearValidate()
      })
    },
    
    // 返回用户列表
    handleBack() {
      this.$router.back()
    }
  }
}
</script>

<style scoped lang="scss">
.user-create-container {
  padding: 0;
  height: 100%;
  overflow-y: auto;
  overflow-x: hidden;
  box-sizing: border-box;

  /* 隐藏滚动条 */
  &::-webkit-scrollbar {
    display: none;
  }
  -ms-overflow-style: none;  /* IE and Edge */
  scrollbar-width: none;  /* Firefox */
}

.control-panel {
  margin-bottom: 24px;
}

.control-container {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
}

.header-info {
  display: flex;
  align-items: center;
  gap: 10px;
  color: #606266;
  font-size: 14px;
  
  i {
    font-size: 18px;
    color: #409eff;
  }
}

.action-wrapper {
  display: flex;
  align-items: center;
  gap: 12px;
}

.user-create-card {
  margin-bottom: 20px;
  border-radius: 8px;
}

.form-module {
  margin-bottom: 30px;
}

.module-title {
  font-size: 15px;
  font-weight: 600;
  color: #303133;
  margin-bottom: 20px;
  padding-left: 10px;
  border-left: 4px solid #409eff;
  line-height: 1;
}

.form-actions {
  margin-top: 40px;
  padding-top: 20px;
  border-top: 1px solid #ebeef5;
  text-align: right;
}

.form-actions button {
  min-width: 100px;
  margin-left: 15px;
}

:deep(.el-form-item__label) {
  font-weight: 500;
  color: #606266;
  padding-bottom: 8px;
}

:deep(.el-input__inner), :deep(.el-textarea__inner) {
  border-radius: 4px;
}

@media screen and (max-width: 768px) {
  .control-container {
    flex-direction: row;
    align-items: center;
  }
  
  .header-info span {
    display: none;
  }
}
</style>